import { useMemo } from 'react';
import { useWorldsStore } from './worlds.store';
import type { Tone } from './components/ui';
import type { Biome, Country, GeoRegion, Marker, Possession, RosterStatus, WorldsState } from './worlds.types';

// ============================================================
// Mondes (NationsGlory) : chiffres de la page (onglets, accueil)
// ============================================================
// Tout est dérivé du store, sans appel : les compteurs suivent le poll et les écritures locales.
// Les filtres de la liste des pays (COUNTRY_FILTERS) sont définis ici pour que l'accueil compte
// exactement ce que l'onglet Pays affichera derrière ?tab=pays&filtre=<clé>.
// ============================================================

// ---- Filtres des pays ----

export type CountryFilterKey = 'actifs' | 'inactifs' | 'sans-centre' | 'hors-territoire' | 'sans-region' | 'sans-chunk' | 'fondes';

export interface CountryFilterCtx {
  /** Noms (slug) des pays fondés en jeu ; null si la possession n'est pas connue. */
  founded: Set<string> | null;
}

export interface CountryFilter {
  key: CountryFilterKey;
  label: string;
  /** Le filtre n'a de sens qu'avec la possession en jeu (NGCountryAPI). */
  needsPossession?: boolean;
  test: (c: Country, ctx: CountryFilterCtx) => boolean;
}

export const COUNTRY_FILTERS: readonly CountryFilter[] = [
  { key: 'actifs', label: 'Actifs', test: (c) => c.enabled },
  { key: 'inactifs', label: 'Inactifs', test: (c) => !c.enabled },
  { key: 'sans-centre', label: 'Sans centre', test: (c) => c.centerX == null || c.centerZ == null },
  { key: 'hors-territoire', label: 'Hors territoire', test: (c) => c.centerInside === false },
  { key: 'sans-region', label: 'Sans région', test: (c) => !c.geoRegionId },
  { key: 'sans-chunk', label: 'Sans chunk', test: (c) => c.chunkCount === 0 },
  { key: 'fondes', label: 'Fondés', needsPossession: true, test: (c, ctx) => !!ctx.founded?.has(c.slug) },
];

export function isCountryFilter(v: string | null | undefined): v is CountryFilterKey {
  return !!v && COUNTRY_FILTERS.some((f) => f.key === v);
}

/** Pays fondés en jeu (par slug), ou null si la possession est inconnue. */
export function foundedSet(possession: Possession | null): Set<string> | null {
  if (!possession?.available) return null;
  return new Set(possession.items.filter((i) => i.takenByFactionId).map((i) => i.name));
}

// ---- Synchro du roster ----

export const ROSTER_STATUS: Record<RosterStatus, { label: string; tone: Tone }> = {
  disabled: { label: 'Synchro non configurée', tone: 'off' },
  idle: { label: 'Rien à synchroniser', tone: 'off' },
  pending: { label: 'Synchro en attente', tone: 'warn' },
  ok: { label: 'Synchronisé en jeu', tone: 'ok' },
  error: { label: 'Synchro en échec', tone: 'bad' },
  blocked: { label: 'Synchro bloquée', tone: 'bad' },
};

// ---- Chiffres ----

export interface WorldsStats {
  countries: {
    /** Liste chargée au moins une fois. */
    loaded: boolean;
    total: number;
    enabled: number;
    withChunks: number;
    noCenter: number;
    centerOutside: number;
    noChunk: number;
    noRegion: number;
    /** Pays fondés en jeu ; null si la possession n'est pas chargée ou indisponible. */
    founded: number | null;
  };
  /** Chunks peints sur le niveau courant. */
  chunks: number;
  regions: {
    loaded: boolean;
    total: number;
    /** Sans aucun pays. */
    empty: number;
    /** Sans pays et sans niveau assigné : sans effet en jeu. */
    idle: number;
  };
  levels: number;
  /** Niveau courant. `loaded` : marqueurs de ce niveau et biomes chargés. */
  markers: { total: number; orphans: number; loaded: boolean };
  roster: {
    status: RosterStatus;
    label: string;
    tone: Tone;
    configured: boolean;
    inSync: boolean;
    lastSuccessAt: string | null;
    lastError: string | null;
  } | null;
}

export interface WorldsStatsInput {
  state: WorldsState | null;
  level: string | null;
  countries: Country[];
  countriesVersion: number;
  geoRegions: GeoRegion[];
  geoRegionsVersion: number;
  levelsCount: number;
  markers: Marker[];
  markersLevel: string | null;
  biomes: Biome[];
  biomesVersion: number;
  possession: Possession | null;
}

/** Calcul pur (testable hors React). */
export function computeWorldsStats(i: WorldsStatsInput): WorldsStats {
  const ctx: CountryFilterCtx = { founded: foundedSet(i.possession) };
  const count = (key: CountryFilterKey) => {
    const f = COUNTRY_FILTERS.find((x) => x.key === key)!;
    return i.countries.reduce((n, c) => n + (f.test(c, ctx) ? 1 : 0), 0);
  };

  const perRegion = new Map<string, number>();
  for (const c of i.countries) if (c.geoRegionId) perRegion.set(c.geoRegionId, (perRegion.get(c.geoRegionId) ?? 0) + 1);
  const emptyRegions = i.geoRegions.filter((r) => !perRegion.get(r.id));

  const levelChunks = i.state?.levels.find((l) => l.name === i.level)?.chunkCount;
  const markersLoaded = !!i.level && i.markersLevel === i.level && i.biomesVersion !== -1;
  const biomeByName = new Map(i.biomes.map((b) => [b.name, b]));
  const markers = markersLoaded ? i.markers : [];

  const sync = i.state?.rosterSync ?? null;
  const meta = sync ? ROSTER_STATUS[sync.status] ?? ROSTER_STATUS.idle : null;

  return {
    countries: {
      loaded: i.countriesVersion !== -1,
      total: i.countries.length,
      enabled: count('actifs'),
      withChunks: i.countries.length - count('sans-chunk'),
      noCenter: count('sans-centre'),
      centerOutside: count('hors-territoire'),
      noChunk: count('sans-chunk'),
      noRegion: count('sans-region'),
      founded: ctx.founded ? count('fondes') : null,
    },
    chunks: levelChunks ?? i.countries.reduce((n, c) => n + c.chunkCount, 0),
    regions: {
      loaded: i.geoRegionsVersion !== -1,
      total: i.geoRegions.length,
      empty: emptyRegions.length,
      idle: emptyRegions.filter((r) => !r.assignedLevel).length,
    },
    levels: i.levelsCount,
    markers: {
      total: markers.length,
      orphans: markers.filter((m) => { const b = biomeByName.get(m.biomeName); return !b || !b.enabled; }).length,
      loaded: markersLoaded,
    },
    roster: sync && meta ? {
      status: sync.status,
      label: meta.label,
      tone: meta.tone,
      configured: sync.configured,
      inSync: sync.inSync,
      lastSuccessAt: sync.lastSuccessAt,
      lastError: sync.lastError,
    } : null,
  };
}

/** Chiffres de la page, recalculés quand une des listes du store change. */
export function useWorldsStats(): WorldsStats {
  const state = useWorldsStore((s) => s.state);
  const level = useWorldsStore((s) => s.level);
  const countries = useWorldsStore((s) => s.countries);
  const countriesVersion = useWorldsStore((s) => s.countriesVersion);
  const geoRegions = useWorldsStore((s) => s.geoRegions);
  const geoRegionsVersion = useWorldsStore((s) => s.geoRegionsVersion);
  const levelsCount = useWorldsStore((s) => s.levels.length);
  const markers = useWorldsStore((s) => s.markers);
  const markersLevel = useWorldsStore((s) => s.markersLevel);
  const biomes = useWorldsStore((s) => s.biomes);
  const biomesVersion = useWorldsStore((s) => s.biomesVersion);
  const possession = useWorldsStore((s) => s.possession);

  return useMemo(() => computeWorldsStats({
    state, level, countries, countriesVersion, geoRegions, geoRegionsVersion, levelsCount,
    markers, markersLevel, biomes, biomesVersion, possession,
  }), [state, level, countries, countriesVersion, geoRegions, geoRegionsVersion, levelsCount,
    markers, markersLevel, biomes, biomesVersion, possession]);
}
